import React, { useRef, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { client, urlFor } from '../client';

const Documentation = () => {
  const scrollRef = useRef(null);
  const [docs, setDocs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // State untuk Drag Gallery Luar
  const [isDown, setIsDown] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  // State untuk Modal
  const [activeGallery, setActiveGallery] = useState(null);
  const [currentImgIndex, setCurrentImgIndex] = useState(0);

  useEffect(() => {
    const fetchDocs = async () => {
      try {
        const query = '*[_type == "event"] | order(_createdAt desc)';
        const data = await client.fetch(query);
        const formattedDocs = data.map((item) => ({
          title: item.title,
          desc: item.location,
          img: item.coverImage ? urlFor(item.coverImage).width(600).url() : '/assets/placeholder.webp',
          images: item.gallery ? item.gallery.map(img => urlFor(img).url()) : []
        }));
        setDocs(formattedDocs);
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
      }
    };
    fetchDocs();
  }, []);

  // --- LOGIKA DRAG GALLERY LUAR ---
  const handleMouseDown = (e) => {
    setIsDown(true);
    setIsDragging(false);
    setStartX(e.pageX - scrollRef.current.offsetLeft);
    setScrollLeft(scrollRef.current.scrollLeft);
  };

  const handleMouseMove = (e) => {
    if (!isDown) return;
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    if (Math.abs(x - startX) > 5) {
      setIsDragging(true);
      scrollRef.current.scrollLeft = scrollLeft - walk;
    }
  };

  const handleMouseUp = (item) => {
    setIsDown(false);
    if (!isDragging) {
      setActiveGallery(item);
      setCurrentImgIndex(0);
    }
  };

  // --- LOGIKA SWIPE/DRAG FOTO DALAM MODAL ---
  const handleDragEnd = (event, info) => {
    const swipeThreshold = 50; 
    if (info.offset.x < -swipeThreshold) {
      setCurrentImgIndex((prev) => (prev + 1) % activeGallery.images.length);
    } else if (info.offset.x > swipeThreshold) {
      setCurrentImgIndex((prev) => (prev - 1 + activeGallery.images.length) % activeGallery.images.length);
    }
  };

  if (isLoading) return <div className="loading-screen"><div className="loader-bar"><div className="loader-progress"></div></div></div>;

  return (
    <section id="documentation" className="doc-section" style={{ padding: '60px 0', width: '100%' }}>
      <div className="container" style={{ textAlign: 'center' }}>
        <h2 className="section-title">Class Documentation</h2>
        
        <div className="doc-scroll-container" style={{ width: '100%', overflow: 'hidden' }}>
          <div 
            className="doc-wrapper"
            ref={scrollRef} 
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseLeave={() => setIsDown(false)}
            onMouseUp={() => setIsDown(false)}
            style={{ 
              display: 'flex', 
              overflowX: 'auto', 
              cursor: isDown ? 'grabbing' : 'grab',
              gap: '30px',
              padding: '40px 20px',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              justifyContent: docs.length > 3 ? 'flex-start' : 'center' 
            }}
          >
            {docs.map((item, index) => (
              <motion.div 
                key={index} 
                className="doc-card"
                onMouseUp={() => handleMouseUp(item)}
                style={{ 
                    flex: '0 0 320px', 
                    userSelect: 'none', 
                    background: 'rgba(255,255,255,0.03)', // Dark Glass Style
                    borderRadius: '24px', 
                    border: '1px solid rgba(255,255,255,0.05)',
                    overflow: 'hidden',
                    transition: 'transform 0.3s'
                }}
              >
                <div className="doc-image" style={{ 
                    backgroundImage: `url(${item.img})`, 
                    height: '220px', 
                    width: '100%',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    filter: 'brightness(0.9)' 
                }}></div>
                <div className="doc-info" style={{ padding: '25px', textAlign: 'left' }}>
                  <h4 style={{ fontSize: '1.2rem', color: '#fff', marginBottom: '8px' }}>{item.title}</h4>
                  <p style={{ color: '#8892b0', fontSize: '0.9rem', lineHeight: '1.5' }}>{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* MODAL POP-UP DENGAN FITUR SWIPE/DRAG */}
      <AnimatePresence>
        {activeGallery && (
          <motion.div 
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setActiveGallery(null)}
            style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.95)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 99999 }}
          >
            <button className="close-btn" style={{ position: 'absolute', top: '30px', right: '40px', color: '#fff', fontSize: '3rem', background: 'none', border: 'none', cursor: 'pointer', zIndex: 100000 }}>&times;</button>

            <motion.div 
              className="doc-modal-content"
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              onClick={(e) => e.stopPropagation()}
              style={{ width: '90%', maxWidth: '800px', textAlign: 'center' }}
            >
              <div className="slider-box" style={{ width: '100%', overflow: 'hidden', position: 'relative', touchAction: 'none' }}>
                <AnimatePresence mode="wait">
                  <motion.img 
                    key={currentImgIndex}
                    src={activeGallery.images[currentImgIndex]} 
                    alt="Gallery Item"
                    drag="x"
                    dragConstraints={{ left: 0, right: 0 }}
                    dragElastic={0.6}
                    onDragEnd={handleDragEnd}
                    initial={{ opacity: 0, x: 100 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -100 }}
                    transition={{ duration: 0.3 }}
                    style={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: '12px', cursor: 'grab' }}
                    whileTap={{ cursor: 'grabbing' }}
                  />
                </AnimatePresence>
              </div>

              <div className="slider-info" style={{ color: '#fff', marginTop: '20px' }}>
                <h3 style={{ fontSize: '1.5rem', marginBottom: '5px' }}>{activeGallery.title}</h3>
                <p style={{ opacity: 0.8 }}>{activeGallery.desc}</p>
                
                {/* --- BAGIAN YANG DIPERBAIKI (ERROR DUPLICATE KEY) --- */}
                <small style={{ 
                  marginTop: '10px', 
                  display: 'inline-block', // Hanya satu 'display' sekarang
                  background: 'rgba(255,255,255,0.2)', 
                  padding: '5px 15px', 
                  borderRadius: '20px' 
                }}>
                  {currentImgIndex + 1} / {activeGallery.images.length}
                </small>
                {/* ----------------------------------------------- */}

              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Documentation;